import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { buildAppLoginUrl } from "@/lib/appLoginUrl";
import Logo from "@/components/Logo";
import LanguageSwitch from "@/components/LanguageSwitch";
import { ArrowUpRight, Upload, Download } from "lucide-react";

export default function Header({ onUpload, onDownload }) {
  const { t, lang } = useLang();
  const loginUrl = buildAppLoginUrl(lang);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";
  const isResult = location.pathname === "/result";
  const landingOverHero = isLanding && !scrolled;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleUpload = () => {
    if (location.pathname === "/") {
      onUpload?.();
      return;
    }
    navigate("/#hero-upload");
  };

  const navLinkClass = landingOverHero
    ? "text-sm font-medium text-white transition hover:text-white/90"
    : "text-sm font-medium text-zinc-600 transition hover:text-zinc-900";

  const loginClass = landingOverHero
    ? "hidden rounded-full px-3.5 py-2 text-xs font-semibold text-white transition hover:text-white/90 sm:inline-flex"
    : "hidden rounded-full px-3.5 py-2 text-xs font-semibold text-zinc-600 transition hover:text-zinc-900 sm:inline-flex";

  if (isLanding) {
    return (
      <header data-testid="site-header" className="fixed inset-x-0 top-0 z-40 px-3 pt-4 sm:px-5">
        <div
          className={`container-x flex h-[72px] items-center justify-between rounded-full border px-4 transition-all duration-300 sm:px-6 ${
            landingOverHero
              ? "border-white/20 shadow-lg shadow-black/15 backdrop-blur-sm"
              : "border-zinc-200 bg-white shadow-md backdrop-blur-xl"
          }`}
          style={
            landingOverHero
              ? { backgroundColor: "var(--sentinel)" }
              : undefined
          }
        >
          <Link to="/" className="flex h-10 items-center" data-testid="logo-link">
            <Logo variant={landingOverHero ? "white" : "green"} className="h-9" />
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            <Link to="/#how" className={navLinkClass} data-testid="nav-how">{t.nav.howItWorks}</Link>
            <Link to="/#trust" className={navLinkClass} data-testid="nav-science">{t.nav.science}</Link>
            <Link to="/#labs" className={navLinkClass} data-testid="nav-labs">{t.nav.labs}</Link>
            <Link to="/#knowledge" className={navLinkClass} data-testid="nav-knowledge">{t.knowledge.overline}</Link>
            <Link to="/#faq" className={navLinkClass} data-testid="nav-faq">{t.nav.faq}</Link>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitch variant={landingOverHero ? "hero" : "default"} />
            <button
              onClick={handleUpload}
              data-testid="header-upload-cta"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition ${
                landingOverHero
                  ? "bg-[color:var(--sentinel)] text-white hover:bg-[color:var(--sentinel-dark)]"
                  : "bg-zinc-900 text-white hover:bg-[color:var(--sentinel)]"
              }`}
            >
              <Upload className="h-3.5 w-3.5" />
              {t.nav.upload}
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "border-b border-zinc-200/70 bg-white/70 backdrop-blur-xl"
          : "border-b border-transparent bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="container-x flex h-[68px] items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          <Link to="/#how" className={navLinkClass} data-testid="nav-how">{t.nav.howItWorks}</Link>
          <Link to="/#trust" className={navLinkClass} data-testid="nav-science">{t.nav.science}</Link>
          <Link to="/#labs" className={navLinkClass} data-testid="nav-labs">{t.nav.labs}</Link>
          <Link to="/#knowledge" className={navLinkClass} data-testid="nav-knowledge">{t.knowledge.overline}</Link>
          <Link to="/#faq" className={navLinkClass} data-testid="nav-faq">{t.nav.faq}</Link>
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <a href={loginUrl} target="_blank" rel="noopener noreferrer" className={loginClass} data-testid="nav-login">
            {t.nav.login}
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </a>
          <button
            onClick={isResult ? onDownload : handleUpload}
            data-testid={isResult ? "header-download-cta" : "header-upload-cta"}
            className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-[color:var(--sentinel)]"
          >
            {isResult ? (
              <>
                <Download className="h-3.5 w-3.5" />
                {t.results.download}
              </>
            ) : (
              <>
                <Upload className="h-3.5 w-3.5" />
                {t.nav.upload}
              </>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
